import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import type { FieldValues, Resolver } from "react-hook-form";
import { useForm } from "react-hook-form";
import type { z } from "zod";

export function useAuthForm<TSchema extends z.ZodType<FieldValues>>(
  schema: TSchema,
  submitHandler: (data: z.infer<TSchema>) => Promise<void>,
) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<z.infer<TSchema>>({
    // biome-ignore lint/suspicious/noExplicitAny: Zod 4 schema types don't match @hookform/resolvers overloads; cast needed for resolver
    resolver: zodResolver(schema as any) as Resolver<z.infer<TSchema>>,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      setIsLoading(true);
      setError(null);
      await submitHandler(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred",
      );
      console.error("Form submission error:", err);
    } finally {
      setIsLoading(false);
    }
  });

  return {
    form,
    onSubmit,
    isLoading,
    error,
    formState: form.formState,
  };
}
